import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthCard, PrimaryAuthButton } from '@/components/AuthCard';
import { ApiError, api } from '@/lib/api';

/**
 * Solicita un email de reset. La respuesta del backend siempre indica éxito
 * (no filtramos qué emails existen). Esta pantalla es pública.
 */
export function PasswordResetRequest() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api<{ message: string }>('/auth/password/reset/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Demasiadas solicitudes. Espera un minuto.');
      } else {
        setError('No se ha podido conectar con el servidor.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <AuthCard
        subtitle="Revisa tu correo."
        meta={
          <Link
            to="/login"
            className="text-[14px] font-medium text-icaria-600 hover:text-icaria-700"
          >
            ← Volver al inicio de sesión
          </Link>
        }
      >
        <div className="flex flex-col gap-3 text-[14px] text-ink-2">
          <p>
            Si <span className="font-mono font-medium">{email}</span> está registrado, recibirás un
            email con instrucciones en los próximos minutos.
          </p>
          <p className="text-[13px] text-muted-foreground">
            ¿No te ha llegado? Revisa la carpeta de spam o solicita otro enlace en un minuto.
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      subtitle="Te enviaremos un enlace para crear una contraseña nueva."
      meta={
        <Link
          to="/login"
          className="text-[14px] font-medium text-icaria-600 hover:text-icaria-700"
        >
          ← Volver al inicio de sesión
        </Link>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu.email@icaria-atelier.com"
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <PrimaryAuthButton submitting={submitting}>
          {submitting ? 'Enviando…' : 'Enviar enlace'}
        </PrimaryAuthButton>
      </form>
    </AuthCard>
  );
}
